/*
 配送日・配送時間セレクターのリファクタ版

 - データの解析、日付計算、DOMレンダリング、保存処理を明確に分離
 - 暗黙的なグローバル変数を避け、settings／holidays を明示的に渡す設計
 - cart（カート）更新に async/await を使用し、localStorage の安全な参照を実現
 - 詳細なコメントを付け、時刻成分のクリアなど日付処理の安全性を向上

 使用方法：
   window.deliverySettings が定義された後にこのスクリプトを読み込むか、
   もしくは settings が存在するタイミングで initDelivery を呼び出す。
*/

(() => {
  'use strict';

  // Constants for element IDs (centralized)
  const IDS = {
    daySelect: 'smart-delivery-date-app__delivery-select',
    timeSelect: 'smart-delivery-date-app__time-select',
    todofukenSelect: 'smart-delivery-date-app__todofuken-select',
    dateLabel: 'smart-delivery-date-app__date-label',
    timeLabel: 'smart-delivery-date-app__time-label'
  };

  const shippingCosts = {
    hokkaido: 1650,
    aomori: 880,
    iwate: 880,
    miyagi: 880,
    akita: 880,
    yamagata: 880,
    fukushima: 880,
    ibaraki: 880,
    tochigi: 880,
    gunma: 880,
    saitama: 880,
    chiba: 880,
    tokyo: 880,
    kanagawa: 880,
    niigata: 880,
    toyama: 880,
    ishikawa: 880,
    fukui: 880,
    yamanashi: 880,
    nagano: 880,
    gifu: 880,
    shizuoka: 880,
    aichi: 880,
    mie: 880,
    shiga: 880,
    kyoto: 880,
    osaka: 880,
    hyogo: 880,
    nara: 880,
    wakayama: 880,
    tottori: 880,
    shimane: 880,
    okayama: 880,
    hiroshima: 880,
    yamaguchi: 880,
    tokushima: 1320,
    kagawa: 1320,
    ehime: 1320,
    kochi: 1320,
    fukuoka: 1320,
    saga: 1320,
    nagasaki: 1320,
    kumamoto: 1320,
    oita: 1320,
    miyazaki: 1320,
    kagoshima: 1320,
    okinawa: 1650
  };

  const WEEKDAYS = ['sun','mon','tue','wed','thu','fri','sat'];

  // Utility: safe JSON parse
  function safeParse(json, fallback) {
    if (json == null) return fallback;
    if (typeof json === 'object') return json;
    try {
      return JSON.parse(json);
    } catch (e) {
      console.warn('safeParse: JSON parse failed, returning fallback', e);
      return fallback;
    }
  }

  // Normalize a Date to midnight local time (for comparisons)
  function toLocalMidnight(date) {
    const d = new Date(date);
    d.setHours(0,0,0,0);
    return d;
  }

  // Compare if two dates have same Y/M/D
  function sameYMD(a,b){
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  }

  // Parse settings from window.deliverySettings into a structured object
  function parseSettings(raw) {
    if (!raw) return null;

    const settings = Object.assign({}, raw);

    settings.deliveryTimeValues = safeParse(settings.deliveryTimeValues, []);
    settings.holidays = safeParse(settings.holidays, {});
    settings.selectedDates = safeParse(settings.selectedDates, []);
    settings.todofuken = safeParse(settings.todofuken, {}); // ✅ 都道府県別設定

    // Ensure numeric fields
    settings.cutoffHour = Number(settings.cutoffHour ?? 0);
    settings.minShippingDays = Number(settings.minShippingDays ?? 0);
    settings.deliveryRangeDays = Number(settings.deliveryRangeDays ?? 14);

    // default display values
    settings.defaultValue = settings.defaultValue ?? '';
    settings.defaultDisplay = settings.defaultDisplay ?? '指定なし';

    // ✅ 都道府県設定が存在するかどうか
    settings.isTodofuken = settings.isTodofuken ?? false;

    return settings;
}


  // Check if weekday key is closed (holidays mapping may be {mon:true, tue:false} or array)
  function isClosed(holidaysMap, date) {
    if (!holidaysMap) return false;
    const key = WEEKDAYS[date.getDay()];
    // support object mapping or array of weekday indexes
    if (Array.isArray(holidaysMap)) {
      return holidaysMap.includes(date.getDay());
    }
    return !!holidaysMap[key];
  }

  // Check if a specific Y/M/D is in the selectedDates array
  function isHolidayClosed(selectedDates, date) {
    if (!selectedDates || selectedDates.length === 0) return false;
    for (const h of selectedDates) {
      if (!h) continue;
      const holidayDate = new Date(h.date || h);
      if (sameYMD(holidayDate, date)) return true;
    }
    return false;
  }

  // Check if date is inside holiday range (start/end inclusive)
  function isInHolidayRange(start, end, date) {
    if (!start || !end) return false;
    const s = toLocalMidnight(new Date(start));
    const e = toLocalMidnight(new Date(end));
    const t = toLocalMidnight(date);
    return t >= s && t <= e;
  }

  // Calculate the earliest selectable delivery date given settings and optionally a starting date
  // returns a Date object set to local midnight
  function calcEarliestDeliveryDate(settings, startFromDate = new Date()){
    if (!settings) throw new Error('settings required');

    // normalize start date
    let date = toLocalMidnight(startFromDate);

    // cutoff hour: if current time is past cutoff, shipments earliest start next day
    const now = new Date();
    if (now.getHours() >= settings.cutoffHour) {
      // move to tomorrow
      date.setDate(date.getDate() + 1);
    }

    // --- (A) まず休暇・休日をすべてスキップ ---
    while (
      isInHolidayRange(settings.holidayRange_start, settings.holidayRange_end, date) ||
      isClosed(settings.holidays, date) ||
      isHolidayClosed(settings.selectedDates, date)
    ) {
      date.setDate(date.getDate() + 1);
    }

    // --- (B) スタート地点が営業日になったので、そこから minShippingDays 分進める ---
    const minDays = Number(settings.minShippingDays ?? 0);
    if (minDays > 0) {
      let added = 0;
      while (added < minDays) {
        date.setDate(date.getDate() + 1); // 次の日に進める
        added += 1;
      }
    }

    return toLocalMidnight(date);
  }

  // Generate an array of selectable dates (Date objects) starting from startDate for rangeDays days
  // Excludes closed days (by holiday/selectedDates) if excludeClosed === true
  function generateSelectableDates(settings, startDate, rangeDays, excludeClosed = false) {
    const dates = [];
    const start = toLocalMidnight(startDate);
    for (let i = 0; i < rangeDays; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      if (excludeClosed) {
        if (isClosed(settings.holidays, d) || isHolidayClosed(settings.selectedDates, d)) continue;
      }
      dates.push(d);
    }
    return dates;
  }

  // Format date by simple tokens similar to original formatDate
  function formatDate(date, format = 'yyyy-MM-dd(EEE)'){
    const y = date.getFullYear();
    const M = String(date.getMonth() + 1).padStart(2,'0');
    const mNoPad = String(date.getMonth() + 1);
    const d = String(date.getDate()).padStart(2,'0');
    const dNoPad = String(date.getDate());

    const weekdaysFull = ['日曜日','月曜日','火曜日','水曜日','木曜日','金曜日','土曜日'];
    const weekdaysShort = ['日','月','火','水','木','金','土'];
    const eeee = weekdaysFull[date.getDay()];
    const eee = weekdaysShort[date.getDay()];

    return format
      .replace('yyyy', y)
      .replace('MM', M)
      .replace('M', mNoPad)
      .replace('dd', d)
      .replace('d', dNoPad)
      .replace('EEEE', eeee)
      .replace('EEE', eee);
  }

  // Render delivery date select options
  function renderDeliverySelect({ settings, startDate, onlyDefault = false }, selectId = IDS.daySelect) {
    const daySelect = document.getElementById(selectId);
    if (!daySelect) return;

    daySelect.innerHTML = '';
    daySelect.name = `attributes[${settings.deliveryKey}]`;

    // default option
    const def = document.createElement('option');
    def.value = settings.defaultValue ?? '';
    def.textContent = settings.defaultDisplay || '指定なし';
    daySelect.appendChild(def);

    if (!onlyDefault) {
      const dates = generateSelectableDates(settings, startDate, settings.deliveryRangeDays || 14, false);
      for (const d of dates) {
        const opt = document.createElement('option');
        opt.value = formatDate(d, settings.deliveryDateFormat || 'yyyy-MM-dd');
        opt.textContent = formatDate(d, settings.deliveryDateDisplayFormat || 'yyyy-MM-dd(EEE)');
        daySelect.appendChild(opt);
      }
      // 選択は保存値で別途復元されるならその処理を行う
      daySelect.selectedIndex = 0;
    } else {
      // only default を表示（selectedIndex 0）
      daySelect.selectedIndex = 0;
    }

    daySelect.addEventListener('change', () => {
      localStorage.setItem('date', daySelect.value);
      localStorage.setItem('dateKey', settings.deliveryKey);
      cartUpdateAsync(settings, daySelect, document.getElementById(IDS.timeSelect));
    });
  }

  // Render time options
  function renderTimeSelect({settings, onlyDefault = false }, selectId = IDS.timeSelect) {
    const timeSelect = document.getElementById(selectId);
    if (!timeSelect) return;

    timeSelect.innerHTML = '';
    timeSelect.name = `attributes[${settings.deliveryTimeKey}]`;

    const def = document.createElement('option');
    def.value = settings.defaultValue;
    def.textContent = settings.defaultDisplay || '指定なし';
    timeSelect.appendChild(def);
    if (!onlyDefault){
      const values = settings.deliveryTimeValues || [];
      for (const v of values) {
        if (!v || v.check === false) continue; // respect original check
        const opt = document.createElement('option');
        opt.value = v.value;
        opt.textContent = v.display || v.value;
        timeSelect.appendChild(opt);
      }

      timeSelect.selectedIndex = 0;
    }
    else{
      timeSelect.selectedIndex = 0;
    }

    timeSelect.addEventListener('change', () => {
      localStorage.setItem('time', timeSelect.value);
      localStorage.setItem('timeKey', settings.deliveryTimeKey);
      // optionally update cart
      cartUpdateAsync(settings, document.getElementById(IDS.daySelect), timeSelect);
    });
  }

  // Async cart update (sends only non-empty attribute values)
  async function cartUpdateAsync(settings, daySelectEl, timeSelectEl) {
    if (!daySelectEl || !timeSelectEl) return;

    const dateNameMatch = daySelectEl.name && daySelectEl.name.match(/\[(.*?)\]/);
    const timeNameMatch = timeSelectEl.name && timeSelectEl.name.match(/\[(.*?)\]/);
    const dateKey = dateNameMatch ? dateNameMatch[1] : null;
    const timeKey = timeNameMatch ? timeNameMatch[1] : null;

    const attributes = {};
    if (dateKey) attributes[dateKey] = daySelectEl.value || '';
    if (timeKey) attributes[timeKey] = timeSelectEl.value || '';

    try {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes })
      });
      const data = await res.json();
      console.log('cartUpdate response', data);
    } catch (err) {
      console.error('cartUpdate failed', err);
    }
  }

  // Validate stored date/time keys — if they mismatch current settings, clear cart attributes
  async function validateLocalKeys(settings) {
    const storedDateKey = localStorage.getItem('dateKey');
    const storedTimeKey = localStorage.getItem('timeKey');

    // if either key is missing, treat as invalid
    if (!storedDateKey || !storedTimeKey) return false;

    if (storedDateKey !== settings.deliveryKey || storedTimeKey !== settings.deliveryTimeKey) {
      // clear stored attributes on cart (if keys exist)
      const body = { attributes: {} };
      body.attributes[storedDateKey] = '';
      body.attributes[storedTimeKey] = '';

      try {
        const res = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        });
        const json = await res.json();
        console.log('Cleared outdated cart attributes', json.attributes);
      } catch (err) {
        console.warn('Failed to clear outdated cart attributes', err);
      }

      return false;
    }

    return true;
  }

  // 公開初期化関数：window.deliverySettings を読み取り、配送日・配送時間セレクターをDOMにマウント
  async function initDelivery() {
    // 設定オブジェクトを取得
    const raw = window.deliverySettings;
    if (!raw) {
      console.warn('initDelivery: window.deliverySettings が見つかりません');
      return;
    }

    // 設定を解析・正規化
    const settings = parseSettings(raw);

    // ラベル設定（DOM上のラベル要素を更新）
    const dateLabel = document.getElementById(IDS.dateLabel);
    const timeLabel = document.getElementById(IDS.timeLabel);
    if (dateLabel) dateLabel.textContent = settings.dateField || dateLabel.textContent;
    if (timeLabel) timeLabel.textContent = settings.timeField || timeLabel.textContent;

    // ローカルストレージに保存されたキーの整合性チェック
    // 旧バージョンや設定変更による不整合がある場合はカート属性をクリア
    const keysOk = await validateLocalKeys(settings);

    // 配送開始日を決定するための変数
    let startDate;

    // 都道府県セレクターを使用する場合の処理
    if (settings.isTodofuken) {
      const todofukenEl = document.getElementById(IDS.todofukenSelect);
      if (todofukenEl) {
        // 保存されている都道府県を取得、未保存なら '-' を初期値とする
        const savedPref = localStorage.getItem('todofuken') ?? '-';
        todofukenEl.value = savedPref;

        const selected = todofukenEl.value;

        if (selected && selected !== '-' && settings.todofuken && settings.todofuken[selected] !== undefined) {
          // 都道府県が選択済みの場合
          // 選択された都道府県に応じて minShippingDays を上書きして最短配送日を計算
          startDate = calcEarliestDeliveryDate(
            Object.assign({}, settings, { minShippingDays: settings.todofuken[selected] })
          );

          // 配送日・配送時間セレクターを描画
          renderDeliverySelect({ settings, startDate, onlyDefault: false });
          renderTimeSelect({ settings, onlyDefault: false });
        } else if (selected === '-') {
          // 「指定なし」に戻した場合 → 配送日は初期値のみ表示
          renderDeliverySelect({ settings, onlyDefault: true });
          renderTimeSelect({ settings, onlyDefault: true });
        } else {
            // 異常系：選択値が不正な場合はセレクターを空に
            const daySel = document.getElementById(IDS.daySelect);
            const timeSel = document.getElementById(IDS.timeSelect);
            if (daySel) daySel.innerHTML = '';
            if (timeSel) timeSel.innerHTML = '';
        }


        if(selected && selected !== '-'){
          const messageEl = document.getElementById('message-shipping-toumei-custom');
          messageEl.textContent = `配送費: ¥${shippingCosts[selected].toLocaleString()}円`;
        }
        else{
          const messageEl = document.getElementById('message-shipping-toumei-custom');
          messageEl.textContent = '配送費は、送り先を選択後に表示されます。。';
        }


        // 都道府県が変更されたときのイベントリスナー登録
        todofukenEl.addEventListener('change', (e) => {
          const val = e.target.value;
          // 選択された都道府県を保存
          localStorage.setItem('todofuken', val);

          if (val && val !== '-' && settings.todofuken && settings.todofuken[val] !== undefined) {
            // 都道府県選択 → その都道府県に応じて配送日を再計算
            const sd = calcEarliestDeliveryDate(
              Object.assign({}, settings, { minShippingDays: settings.todofuken[val] })
            );
            renderDeliverySelect({ settings, startDate: sd, onlyDefault: false });
            renderTimeSelect({ settings, onlyDefault: false });

          } else if (val === '-') {
            // 「指定なし」に戻した場合 → 配送日は初期値のみ表示
            renderDeliverySelect({ settings, startDate, onlyDefault: false });
            renderTimeSelect({ settings, startDate, onlyDefault: false });
          } else {
            // 異常系：選択値が不正な場合はセレクターを空に
            const daySel = document.getElementById(IDS.daySelect);
            const timeSel = document.getElementById(IDS.timeSelect);
            if (daySel) daySel.innerHTML = '';
            if (timeSel) timeSel.innerHTML = '';
          }

          if(val && val !== '-'){
            const messageEl = document.getElementById('message-shipping-toumei-custom');
            messageEl.textContent = `配送費: ¥${shippingCosts[val].toLocaleString()}円`;
          }
          else{
            const messageEl = document.getElementById('message-shipping-toumei-custom');
            messageEl.textContent = '配送費は、送り先を選択後に表示されます。。';
          }
        });


      }
    }
    // 都道府県未使用の場合
    // 通常の最短配送日を計算して使用
    if (!startDate) startDate = calcEarliestDeliveryDate(settings);

    // 配送日・配送時間セレクターを描画（都道府県未使用または fallback）
    renderDeliverySelect({ settings, startDate, onlyDefault: false });
    renderTimeSelect({ settings, onlyDefault: false });

    // 保存されていた配送日・時間を復元（ローカルストレージのキーが一致している場合）
    if (keysOk) {
      const savedDate = localStorage.getItem('date');
      const savedTime = localStorage.getItem('time');
      const daySel = document.getElementById(IDS.daySelect);
      const timeSel = document.getElementById(IDS.timeSelect);
      if (daySel && savedDate) daySel.value = savedDate;
      if (timeSel && savedTime) timeSel.value = savedTime;
    } else {
      // キーが不一致の場合はセレクターを初期値に戻す
      const daySel = document.getElementById(IDS.daySelect);
      const timeSel = document.getElementById(IDS.timeSelect);
      if (daySel) daySel.value = settings.defaultValue;
      if (timeSel) timeSel.value = settings.defaultValue;
    }

    // デバッグ用や外部制御用に API を window に公開
    window.smartDelivery = {
      settings,
      calcEarliestDeliveryDate,
      renderDeliverySelect,
      renderTimeSelect,
      cartUpdateAsync
    };

    console.log('smartDelivery initialized', settings);
  }

  // Auto-init on DOMContentLoaded
  document.addEventListener('DOMContentLoaded', () => {
    // Try to init; if deliverySettings isn't available yet, wait a tick (some stores inject later)
    if (window.deliverySettings) {
      initDelivery();
    } else {
      // Poll a few times for settings to appear (safe and short)
      let attempts = 0;
      const t = setInterval(() => {
        attempts += 1;
        if (window.deliverySettings) {
          clearInterval(t);
          initDelivery();
        } else if (attempts > 10) {
          clearInterval(t);
          console.warn('deliverySettings not found after waiting');
        }
      }, 200);
    }
  });
  window.initDelivery = initDelivery;

})();
